import { Dialog, Transition } from '@headlessui/react'
import { ExclamationIcon, InformationCircleIcon, XIcon } from '@heroicons/react/outline'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/solid'
import classNames from 'classnames'
import _, { random, upperFirst, values } from 'lodash'
import { Fragment, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import Selection from 'components/molecules/Selection'

const Alert = ({ title, message, id, type, error }) => {
  const [show, setShow] = useState(false)
  const { t } = useTranslation()

  const getValidationFieldLabel = rawField => {
    if (!rawField) return ''
    const key = _.toLower(String(rawField).replace(/['"]/g, ''))

    switch (key) {
      case 'color':
        return t('carAuth.color')
      case 'dailyrate':
        return t('carAuth.dailyRate')
      case 'yearlyrate':
        return t('carAuth.yearlyRate')
      case 'deliverycost':
        return t('carAuth.deliveryCost')
      case 'depositamount':
        return t('carAuth.depositAmount')
      case 'allowedkmperday':
        return t('carAuth.allowedKMPerDay')
      case 'insurancefee':
        return t('carAuth.insuranceFee')
      case 'insurance':
        return t('carAuth.insurance')
      case 'fuelpolicy':
        return t('carAuth.fuelPolicy')
      case 'modelyear':
        return t('carAuth.modalYear')
      case 'company':
        return t('carAuth.company')
      case 'branch':
        return t('carAuth.branch')
      case 'monthlyrate':
        return t('carAuth.monthlyRate')
      case 'status':
        return t('carAuth.status')
      case 'weeklyrate':
        return t('carAuth.weeklyRate')
      case 'make':
        return t('carAuth.make')
      case 'model':
        return t('carAuth.model')
      case 'numberofseats':
        return t('carAuth.numberOfSeats')
      case 'numberofdoors':
        return t('carAuth.numberOfDoors')
      case 'shifter':
        return t('carAuth.shifter')
      case 'marketsegment':
        return t('carAuth.marketSegment')
      case 'enginesize':
        return t('carAuth.engineSize')
      case 'dailydiscountrate':
        return t('carAuth.dailyDiscountRate')
      case 'weeklydiscountrate':
        return t('carAuth.weeklyDiscountRate')
      case 'monthlydiscountrate':
        return t('carAuth.monthlyDiscountRate')
      case 'yearlydiscountrate':
        return t('carAuth.yearlyDiscountRate')
      default:
        return upperFirst(rawField)
    }
  }

  const localizeValidationMessage = raw => {
    if (!raw || !_.isString(raw)) return raw

    const cleaned = raw.replace(/['"]/g, '')

    const requiredMatch = cleaned.match(/^(.+?) is not allowed to be empty$/i)
    if (requiredMatch) {
      const fieldLabel = getValidationFieldLabel(requiredMatch[1])
      return t('validation.messages.required', { field: fieldLabel })
    }

    const numberMatch = cleaned.match(/^(.+?) must be a number$/i)
    if (numberMatch) {
      const fieldLabel = getValidationFieldLabel(numberMatch[1])
      return t('validation.messages.mustBeNumber', { field: fieldLabel })
    }

    const lessThanMatch = cleaned.match(/^(.+?) must be less than ref:(.+?)$/i)
    if (lessThanMatch) {
      const fieldLabel = getValidationFieldLabel(lessThanMatch[1])
      const compareFieldLabel = getValidationFieldLabel(lessThanMatch[2])
      return t('validation.messages.mustBeLessThan', { field: fieldLabel, compareField: compareFieldLabel })
    }

    return cleaned
  }

  const localizedTitle = title && _.toLower(title) === 'validation fail' ? t('validation.title') : title

  const getErrorList = rawError => {
    if (!rawError) return []

    let parsed = rawError
    if (_.isString(rawError)) {
      try {
        parsed = JSON.parse(rawError)
      } catch {
        return [rawError]
      }
    }

    if (_.isArray(parsed)) {
      return _.compact(_.flatMap(parsed, value => (_.isArray(value) ? value : [value])))
    }

    if (_.isObject(parsed)) {
      return _.compact(_.flatMap(values(parsed), value => (_.isArray(value) ? value : [value])))
    }

    return [String(parsed)]
  }

  const errorMessages = getErrorList(error)

  useEffect(() => {
    setTimeout(() => {
      setShow(true)
    }, 100)
  }, [])

  useEffect(() => {
    setTimeout(
      () => {
        setShow(false)
      },
      message?.length > 100 ? 6000 : 3000
    )

    setTimeout(() => {
      document.getElementById(id)?.remove()
    }, 4000)
  }, [])
  return (
    <div aria-live="assertive" className="fixed z-[20000] inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start">
      <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
        <Transition
          show={show}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:-translate-y-12"
          enterTo="translate-y-0 opacity-100 sm:translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0 sm:translate-y-0"
          leaveTo="opacity-0 translate-y-2 sm:-translate-y-12"
        >
          <div className="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {type === 'success' && <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />}
                  {type === 'error' && <ExclamationCircleIcon className="h-6 w-6 text-red-400" aria-hidden="true" />}
                  {type === 'warning' && <ExclamationCircleIcon className="h-6 w-6 text-yellow-400" aria-hidden="true" />}
                </div>
                <div className="min-w-0 flex-1">
                  {localizedTitle ? <p className="text-sm font-medium leading-5 text-gray-900">{localizedTitle}</p> : null}
                  {message ? <p className="mt-1 text-sm leading-5 text-gray-500">{message}</p> : null}
                  {errorMessages.length > 0 ? (
                    <div className="mt-2 space-y-1">
                      {errorMessages.map((item, index) => (
                        <p className="text-sm leading-5 text-gray-500" key={`${item}-${index}`}>
                          {localizeValidationMessage(item)}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="flex-shrink-0">
                  <button
                    className="bg-white z-50 rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={e => {
                      e.stopPropagation()
                      e.preventDefault()
                      setShow(false)
                      setTimeout(() => {
                        document.getElementById(id).remove()
                      }, 100)
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  )
}

const useUnmountWhenClosed = (open, container) => {
  useEffect(() => {
    if (open || !container) return
    const timer = setTimeout(() => ReactDOM.unmountComponentAtNode(container), 300)
    return () => clearTimeout(timer)
  }, [open, container])
}

const Confirm = ({ id, container, type = 'delete', onConfirm, onCancel, title, autoClose, message, dangerText, cancelText, t }) => {
  const [open, setOpen] = useState(true)
  const cancelButtonRef = useRef(null)
  useUnmountWhenClosed(open, container)
  useEffect(() => {
    if (autoClose) {
      setTimeout(() => {
        setOpen(true)
      }, 100)
      setTimeout(() => {
        setOpen(false)
      }, 3000)
      document.getElementById(id)?.remove()
    } else {
      setOpen(true)
      document.getElementById(id)?.remove()
    }
  }, [])
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={setOpen}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div
                  className={classNames(
                    type === 'error' && 'bg-red-100',
                    type === 'alert' && 'bg-gray-100',
                    'mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full  sm:mx-0 sm:h-10 sm:w-10'
                  )}
                >
                  {type === 'error' && <ExclamationIcon className="h-6 w-6 text-red-600" aria-hidden="true" />}
                  {type === 'alert' && <InformationCircleIcon className="h-6 w-6 text-dark-600" aria-hidden="true" />}
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {title}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{message}</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-3 rtl:sm:flex-row">
                <button
                  type="button"
                  className={classNames(
                    type === 'error' && 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
                    type === 'alert' && 'bg-dark-600 hover:bg-dark-700 focus:ring-dark-500',
                    'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-2.5 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm transition-colors duration-200'
                  )}
                  onClick={() => {
                    onConfirm()
                    setOpen(false)
                  }}
                >
                  {dangerText || (t ? t('common.deleteConfirm.confirm') : 'Confirm')}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-6 py-2.5 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm transition-colors duration-200"
                  onClick={() => {
                    if (onCancel) onCancel()
                    setOpen(false)
                  }}
                  ref={cancelButtonRef}
                >
                  {cancelText || (t ? t('common.deleteConfirm.cancel') : 'Cancel')}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
const CancelConfirm = ({
  id,
  container,
  type = 'delete',
  onConfirm,
  title,
  autoClose,
  message,
  dangerText,
  showRefundOptions = false,
  showNotificationOptions = true,
  isPaymob = false,
  refundOnly = false,
  maxRefundAmount,
  refundSummary,
  bookingTotal,
  showPolicyOption = false,
  walletOnly = false,
  walletShare = 0,
  t,
}) => {
  const { t: tr } = useTranslation()
  const [open, setOpen] = useState(true)
  const [selectedRefundMethod, setSelectedRefundMethod] = useState(refundOnly ? 'refund' : 'none')
  const [sendCancellationNotification, setSendCancellationNotification] = useState(true)
  const [rrnNumber, setRrnNumber] = useState('')
  const [rrnError, setRrnError] = useState('')
  const [refundAmount, setRefundAmount] = useState('')
  const [refundAmountError, setRefundAmountError] = useState('')
  const cancelButtonRef = useRef(null)
  useUnmountWhenClosed(open, container)

  const allRefundOptions = [
    { value: 'none', label: 'None' },
    isPaymob
      ? {
          value: 'refund',
          label: 'Refund',
          description: walletShare
            ? `The card share is refunded through Paymob and the ${Number(walletShare).toFixed(3)} OMR paid from the wallet goes back to the wallet.`
            : "Refunded to the customer's card through Paymob. Any amount already returned to the wallet for this booking is deducted from the refund.",
        }
      : { value: 'refund', label: 'Refund', description: 'Request a Thawani refund. Any wallet credit previously added for this booking will be deducted first.' },
    isPaymob
      ? {
          value: 'void',
          label: 'Void',
          description: `Cancels the Paymob payment, or refunds the available amount if a full void is not possible.${
            walletShare ? ` The ${Number(walletShare).toFixed(3)} OMR paid from the wallet goes back to the wallet.` : ''
          }`,
        }
      : { value: 'void', label: 'Void', description: "Voids today's card payment." },
    { value: 'wallet', label: 'Wallet', description: "Adds the booking amount to the customer's wallet." },
    // Refund engine: the caller opens its own reason/facts dialog with a preview before cancelling.
    ...(showPolicyOption ? [{ value: 'policy', label: tr('refundEngine.booking.policyOption'), description: tr('refundEngine.booking.policyOptionHint') }] : []),
  ]
  // Further Paymob refund on an already-cancelled booking: only the refund option applies.
  // Paid fully from the wallet: no card payment to refund or void, the money can only go back to the wallet.
  const refundOptions = refundOnly
    ? allRefundOptions.filter(option => option.value === 'refund')
    : walletOnly
    ? allRefundOptions.filter(option => _.includes(['none', 'wallet', 'policy'], option.value))
    : allRefundOptions
  // eslint-disable-next-line
  const selectedRefundOption = refundOptions.find(option => option.value === selectedRefundMethod)
  const isRefundMethod = selectedRefundMethod === 'refund'

  useEffect(() => {
    if (autoClose) {
      setTimeout(() => {
        setOpen(true)
      }, 100)

      setTimeout(() => {
        setOpen(false)
      }, 3000)

      document.getElementById(id)?.remove()
    } else {
      setOpen(true)
      document.getElementById(id)?.remove()
    }
  }, [])
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={setOpen}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div
                  className={classNames(
                    type === 'error' && 'bg-red-100',
                    type === 'alert' && 'bg-gray-100',
                    'mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full  sm:mx-0 sm:h-10 sm:w-10'
                  )}
                >
                  {type === 'error' && <ExclamationIcon className="h-6 w-6 text-red-600" aria-hidden="true" />}
                  {type === 'alert' && <InformationCircleIcon className="h-6 w-6 text-dark-600" aria-hidden="true" />}
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {title}
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-500">{message}</p>
                </div>
              </div>

              <div className="mt-5 space-y-4 sm:pl-14">
                {showRefundOptions && (
                  <div>
                    <span className="block text-sm font-medium text-gray-700">Refund method</span>
                    <Selection
                      className="mt-1 w-full"
                      value={selectedRefundMethod}
                      list={refundOptions.map(option => ({ id: option.value, name: option.label }))}
                      title="Select refund method"
                      onChange={item => {
                        setSelectedRefundMethod(item.id)
                        setRrnError('')
                        setRefundAmountError('')
                      }}
                    />
                    {selectedRefundOption?.description && <p className="mt-1 text-xs text-gray-500">{selectedRefundOption.description}</p>}
                  </div>
                )}

                {showRefundOptions && (isRefundMethod || selectedRefundMethod === 'void') && isPaymob && refundSummary && (
                  <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
                    <span className="text-sm text-gray-600">Available to refund</span>
                    <span className="text-sm font-semibold text-gray-900">{Number(refundSummary.maximumRefundable).toFixed(3)} OMR</span>
                  </div>
                )}

                {showRefundOptions && (isRefundMethod || selectedRefundMethod === 'void') && isPaymob && walletShare > 0 && (
                  <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
                    <span className="text-sm text-gray-600">Returned to wallet</span>
                    <span className="text-sm font-semibold text-gray-900">{Number(walletShare).toFixed(3)} OMR</span>
                  </div>
                )}

                {showRefundOptions && isRefundMethod && isPaymob && (
                  <div>
                    <div className="flex items-center justify-between">
                      <label htmlFor="cancel-refund-amount" className="block text-sm font-medium text-gray-700">
                        Refund amount
                      </label>
                      {maxRefundAmount !== undefined && (
                        <button
                          type="button"
                          className="text-xs font-medium text-blue-600 hover:text-blue-700"
                          onClick={() => {
                            setRefundAmount(Number(maxRefundAmount).toFixed(3))
                            setRefundAmountError('')
                          }}
                        >
                          Max
                        </button>
                      )}
                    </div>
                    <div className="relative mt-1">
                      <input
                        id="cancel-refund-amount"
                        type="number"
                        min="0"
                        max={maxRefundAmount}
                        step="0.001"
                        value={refundAmount}
                        onChange={e => {
                          setRefundAmount(e.target.value)
                          if (refundAmountError) setRefundAmountError('')
                        }}
                        placeholder={maxRefundAmount !== undefined ? Number(maxRefundAmount).toFixed(3) : '0.000'}
                        className={classNames(
                          'block w-full rounded-md border py-2 pl-3 pr-14 text-sm shadow-sm focus:outline-none focus:ring-1',
                          refundAmountError ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                        )}
                      />
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500">OMR</span>
                    </div>
                    {refundAmountError ? (
                      <p className="mt-1 text-xs text-red-600">{refundAmountError}</p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">Leave empty to refund the full available amount.</p>
                    )}
                  </div>
                )}

                {showRefundOptions && isRefundMethod && !isPaymob && (
                  <div>
                    <label htmlFor="cancel-refund-rrn" className="block text-sm font-medium text-gray-700">
                      RNN number <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="cancel-refund-rrn"
                      type="text"
                      value={rrnNumber}
                      maxLength={100}
                      onChange={e => {
                        setRrnNumber(e.target.value)
                        if (rrnError) setRrnError('')
                      }}
                      placeholder="Enter RNN number"
                      className={classNames(
                        'mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1',
                        rrnError ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      )}
                    />
                    {rrnError ? <p className="mt-1 text-xs text-red-600">{rrnError}</p> : null}
                  </div>
                )}

                {showNotificationOptions && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      checked={sendCancellationNotification}
                      onChange={e => setSendCancellationNotification(e.target.checked)}
                    />
                    Notify customer about the cancellation
                  </label>
                )}
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto"
                  onClick={() => {
                    setOpen(false)
                  }}
                  ref={cancelButtonRef}
                >
                  {t ? t('common.deleteConfirm.cancel') : 'Cancel'}
                </button>
                <button
                  type="button"
                  className={classNames(
                    type === 'error' && 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
                    type === 'alert' && 'bg-dark-600 hover:bg-dark-700 focus:ring-dark-500',
                    'inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto'
                  )}
                  onClick={() => {
                    if (showRefundOptions) {
                      if (isRefundMethod && isPaymob) {
                        // Paymob: optional amount (empty = full remaining); the backend enforces the refundable maximum.
                        // eslint-disable-next-line
                        const trimmedAmount = String(refundAmount).trim()
                        if (trimmedAmount && !(Number(trimmedAmount) > 0)) {
                          setRefundAmountError('Refund amount must be greater than 0')
                          return
                        }
                        // Never more than the booking total, nor the remaining refundable amount (the backend enforces both).
                        if (trimmedAmount && bookingTotal !== undefined && Number(trimmedAmount) > Number(bookingTotal)) {
                          setRefundAmountError(`Refund amount cannot be greater than the booking total (${Number(bookingTotal).toFixed(3)} OMR)`)
                          return
                        }
                        if (trimmedAmount && maxRefundAmount !== undefined && Number(trimmedAmount) > Number(maxRefundAmount)) {
                          setRefundAmountError(`Refund amount cannot be more than the remaining refund amount (${Number(maxRefundAmount).toFixed(3)} OMR)`)
                          return
                        }
                        onConfirm({ method: 'refund', refundAmount: trimmedAmount ? Number(trimmedAmount) : undefined }, sendCancellationNotification)
                      } else if (isRefundMethod) {
                        // eslint-disable-next-line
                        const trimmedRrn = rrnNumber.trim()
                        if (!trimmedRrn) {
                          setRrnError('RNN number is required')
                          return
                        }
                        onConfirm({ method: 'refund', rrn: trimmedRrn }, sendCancellationNotification)
                      } else {
                        onConfirm(selectedRefundMethod === 'none' ? null : selectedRefundMethod, sendCancellationNotification)
                      }
                    } else if (showNotificationOptions) {
                      onConfirm(null, sendCancellationNotification)
                    } else {
                      onConfirm()
                    }
                    setOpen(false)
                  }}
                >
                  {dangerText || (t ? t('common.deleteConfirm.confirm') : 'Confirm')}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

const CopyPanel = ({ id, container, type = 'delete', onConfirm, title, autoClose, message, dangerText }) => {
  const [open, setOpen] = useState(true)
  const cancelButtonRef = useRef(null)
  useUnmountWhenClosed(open, container)

  useEffect(() => {
    if (autoClose) {
      setTimeout(() => {
        setOpen(true)
      }, 100)

      setTimeout(() => {
        setOpen(false)
      }, 3000)

      document.getElementById(id)?.remove()
    } else {
      setOpen(true)
      document.getElementById(id)?.remove()
    }
  }, [])
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={setOpen}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-[60%] sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {title}
                  </Dialog.Title>
                </div>
              </div>
              <div className="mt-5">
                <div className="rounded-md bg-gray-50 px-6 py-5 sm:flex sm:items-start sm:justify-between">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 sm:mt-0 flex sm:ml-0">
                      <div className="mt-1 w-[80%] sm:mt-0">{message}</div>
                      <button
                        onClick={onConfirm}
                        type="button"
                        className="inline-flex absolute right-8 top-[53%] items-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

const notify = {
  success: (title, message) => {
    if (title) {
      const id = random(0, 100000)
      const el = document.createElement('DIV')
      el.id = id
      ReactDOM.render(<Alert type="success" title={title} message={message} id={id} />, document.body.appendChild(el))
    }
  },
  warning: (title, message) => {
    if (title) {
      const id = random(0, 100000)
      const el = document.createElement('DIV')
      el.id = id
      ReactDOM.render(<Alert type="warning" title={title} message={message} id={id} />, document.body.appendChild(el))
    }
  },
  error: (title = '', message = '', error) => {
    if (title) {
      _.forEach(document.querySelectorAll('[data-notify-alert-root]'), node => node.remove())

      const id = String(random(0, 100000))
      const el = document.createElement('div')
      el.id = id
      el.setAttribute('data-notify-alert-root', 'true')
      ReactDOM.render(<Alert type="error" title={title} error={error} message={message} id={id} />, document.body.appendChild(el))
    }
  },
  deleteConfirm: ({ title, onConfirm, autoClose = false, message, t, ...rest }) => {
    const id = random(0, 100000)
    const el = document.createElement('DIV')
    el.id = id
    ReactDOM.render(
      <Confirm type="error" title={title} onConfirm={onConfirm} autoClose={autoClose} message={message} t={t} id={id} container={el} {...rest} />,
      document.body.appendChild(el)
    )
  },
  deleteCancelConfirm: ({ title, onConfirm, autoClose = false, message, showRefundOptions = false, showNotificationOptions = true, t, ...rest }) => {
    const id = random(0, 100000)
    const el = document.createElement('DIV')
    el.id = id
    ReactDOM.render(
      <CancelConfirm
        type="error"
        title={title}
        onConfirm={onConfirm}
        autoClose={autoClose}
        message={message}
        showRefundOptions={showRefundOptions}
        showNotificationOptions={showNotificationOptions}
        t={t}
        id={id}
        container={el}
        {...rest}
      />,
      document.body.appendChild(el)
    )
  },
  alertConfirm: ({ title, onConfirm, message, ...rest }) => {
    const id = random(0, 100000)
    const el = document.createElement('DIV')
    el.id = id
    ReactDOM.render(<Confirm type="alert" title={title} onConfirm={onConfirm} message={message} id={id} container={el} {...rest} />, document.body.appendChild(el))
  },
  copyUrlPanel: ({ title, message, onConfirm, ...rest }) => {
    const id = random(0, 100000)
    const el = document.createElement('DIV')
    el.id = id
    ReactDOM.render(<CopyPanel title={title} message={message} onConfirm={onConfirm} id={id} container={el} {...rest} />, document.body.appendChild(el))
  },
}

export default notify
